import React, { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { FormikErrors, FormikProps, FormikState } from 'formik';

import { Contact } from '@/types/contact';
import { classifiedSort } from '@/utils/name';
import { RequiredMark } from '@/components/shared/RequiredMark';
import { Label } from '@/components/shared/Label';
import { Input } from '@/components/shared/Input';

import * as styles from './index.module.scss';

export type Values = Contact;

type Props = {
  errors: FormikErrors<Values>;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement>) => void;
  touched: FormikProps<Values>['touched'];
  values: Values;
  isConnecting: boolean;
  setSubmitting: (isSubmitting: boolean) => void;
  resetForm: (nextState?: Partial<FormikState<Values>>) => void;
  setFieldTouched: (field: keyof Values, touched: boolean) => void;
  handleChange: (e: React.ChangeEvent<any>) => void;
};

export const FormContents: React.FC<Props> = ({
  errors,
  handleSubmit,
  touched,
  values,
  isConnecting,
  setSubmitting,
  resetForm,
  setFieldTouched,
  handleChange,
}) => {
  const { t } = useTranslation();

  useEffect(() => {
    if (isConnecting) {
      setSubmitting(false);
      resetForm({ values });
    }
  }, [isConnecting, resetForm, setSubmitting, values]);

  const onChangeWithTouched = useCallback(
    (e: React.ChangeEvent<any>) => {
      setFieldTouched(e.currentTarget.name, true);
      handleChange(e);
    },
    [handleChange, setFieldTouched],
  );
  return (
    <div>
      <div className={styles.requiredNote}>
        <p>{t('ui.expression.requiredNote')}</p>
      </div>
      <form onSubmit={handleSubmit} id="address-form">
        <div className={styles.field}>
          <label>
            {t('models.contact.corporateName')}
            <RequiredMark />
          </label>
          <Input
            data-test="corporate-name"
            className={classnames({
              'is-error': errors.corporationName && touched.corporationName,
            })}
            placeholder={t('ui.placeholder.corporateName')}
            name="corporationName"
            onChange={onChangeWithTouched}
            value={values.corporationName}
          />
          {touched.corporationName && errors.corporationName && (
            <Label>{errors.corporationName}</Label>
          )}
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.department')}
            <RequiredMark />
          </label>
          <Input
            data-test="department"
            className={classnames({
              'is-error': errors.departmentName && touched.departmentName,
            })}
            placeholder={t('ui.placeholder.departmentName')}
            name="departmentName"
            onChange={onChangeWithTouched}
            value={values.departmentName}
          />
          {touched.departmentName && errors.departmentName && (
            <Label>{errors.departmentName}</Label>
          )}
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.postalCode')}
            <RequiredMark />
          </label>
          <Input
            data-test="postal-code"
            className={classnames({
              'is-error': errors.postalCode && touched.postalCode,
            })}
            placeholder={t('ui.placeholder.postalCode')}
            name="postalCode"
            onChange={onChangeWithTouched}
            value={values.postalCode}
          />
          {touched.postalCode && errors.postalCode && (
            <Label>{errors.postalCode}</Label>
          )}
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.address')}
            <RequiredMark />
          </label>
          <Input
            data-test="address"
            className={classnames({
              'is-error': errors.address && touched.address,
            })}
            placeholder={t('ui.placeholder.address')}
            name="address"
            onChange={onChangeWithTouched}
            value={values.address}
          />
          {touched.address && errors.address && <Label>{errors.address}</Label>}
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.personInCharge')}
            <RequiredMark />
          </label>
          <div className={styles.equalGroup}>
            {classifiedSort([
              <div className={styles.field} key="first-name">
                <Input
                  data-test="first-name"
                  className={classnames({
                    'is-error': errors.firstName && touched.firstName,
                  })}
                  placeholder={t('ui.expression.firstName')}
                  name="firstName"
                  onChange={onChangeWithTouched}
                  value={values.firstName}
                />
                {touched.firstName && errors.firstName && (
                  <Label classNames={'parallel-elem'}>{errors.firstName}</Label>
                )}
              </div>,
              <div className={styles.field} key="last-name">
                <Input
                  data-test="last-name"
                  className={classnames({
                    'is-error': errors.lastName && touched.lastName,
                  })}
                  placeholder={t('ui.expression.lastName')}
                  name="lastName"
                  onChange={onChangeWithTouched}
                  value={values.lastName}
                />
                {touched.lastName && errors.lastName && (
                  <Label classNames={'parallel-elem'}>{errors.lastName}</Label>
                )}
              </div>,
            ])}
          </div>
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.phonetic')}
            {t('ui.expression.phoneticNote')}
            <RequiredMark />
          </label>
          <div className={styles.equalGroup}>
            {classifiedSort([
              <div className={styles.field} key="kana-first-name">
                <Input
                  data-test="kana-first-name"
                  className={classnames({
                    'is-error': errors.kanaFirstName && touched.kanaFirstName,
                  })}
                  placeholder={t('ui.expression.firstNamePhonetic')}
                  name="kanaFirstName"
                  onChange={onChangeWithTouched}
                  value={values.kanaFirstName}
                />
                {touched.kanaFirstName && errors.kanaFirstName && (
                  <Label classNames={'parallel-elem'}>
                    {errors.kanaFirstName}
                  </Label>
                )}
              </div>,
              <div className={styles.field} key="kana-last-name">
                <Input
                  data-test="kana-last-name"
                  className={classnames({
                    'is-error': errors.kanaLastName && touched.kanaLastName,
                  })}
                  placeholder={t('ui.expression.lastNamePhonetic')}
                  name="kanaLastName"
                  onChange={onChangeWithTouched}
                  value={values.kanaLastName}
                />
                {touched.kanaLastName && errors.kanaLastName && (
                  <Label classNames={'parallel-elem'}>
                    {errors.kanaLastName}
                  </Label>
                )}
              </div>,
            ])}
          </div>
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.phoneNumber')}
            <RequiredMark />
          </label>
          <Input
            data-test="phone-number"
            className={classnames({
              'is-error': errors.phoneNumber && touched.phoneNumber,
            })}
            placeholder={t('ui.placeholder.phoneNumber')}
            name="phoneNumber"
            onChange={onChangeWithTouched}
            value={values.phoneNumber}
          />
          {touched.phoneNumber && errors.phoneNumber && (
            <Label>{errors.phoneNumber}</Label>
          )}
        </div>
        <div className={styles.field}>
          <label>
            {t('models.contact.email')}
            <RequiredMark />
          </label>
          <Input
            data-test="email"
            className={classnames({
              'is-error': errors.email && touched.email,
            })}
            placeholder={t('ui.placeholder.email')}
            name="email"
            onChange={onChangeWithTouched}
            value={values.email}
          />
          {touched.email && errors.email && <Label>{errors.email}</Label>}
        </div>
        <button id="address-form-submit-button" style={{ display: 'none' }} />
      </form>
    </div>
  );
};
